import React, { useCallback, useEffect, useMemo, useState } from 'react';
import VisitaMapModal from '../components/VisitaMapModal';
import { fixText } from '../utils/textEncoding';
import { api } from '../services/api';
import useInvalidate from '../hooks/useInvalidate';
import {
  formatColombiaTime,
  formatHace,
  parseInstant,
} from '../utils/colombiaTime';

function parsePuntos(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}

function mapsUrl(lat, lng) {
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

/** Sin señal reciente / fuera de "en campo" (solo-GPS) si el último ping > N min. */
const GPS_STALE_MS = 5 * 60 * 1000;
/** < 2 min → "Señal reciente" (GPS vivo; NO "app abierta"). */
const GPS_FRESH_MS = 2 * 60 * 1000;

function fmtHace(isoOrMs) {
  if (isoOrMs == null || isoOrMs === '') return null;
  if (typeof isoOrMs === 'number') return formatHace(isoOrMs);
  return formatHace(isoOrMs);
}

/** Etiqueta de frescura GPS (admin). Nunca "Con la app abierta". */
function gpsFreshnessText({ stale, ageMs, ageLabel }) {
  if (stale) {
    return ageLabel ? `Sin señal reciente (${ageLabel})` : 'Sin señal reciente';
  }
  if (Number.isFinite(ageMs) && ageMs < GPS_FRESH_MS) return 'Señal reciente';
  return ageLabel || 'Señal reciente';
}

/** Solo-GPS sin ping reciente: no listar en "en campo". Visitas abiertas sí se mantienen. */
function isActivaEnCampo(v) {
  if (!v?.live_only) return true;
  const at = v.vendedor_ubicacion_at || v.inicio;
  if (!at) return false;
  const d = parseInstant(at);
  if (!d) return false;
  const ageMs = Date.now() - d.getTime();
  return ageMs <= GPS_STALE_MS;
}

function ultimaUbicacion(v) {
  const puntos = parsePuntos(v.puntos_gps);
  const valid = puntos.filter((p) => p.lat != null && p.lng != null);
  let lat = null;
  let lng = null;
  let at = null;
  let accuracy = null;
  let puntosCount = valid.length;

  // Ping vivo del vendedor (nunca usar solo un punto viejo de visita / WiFi casa).
  const liveParsed = parseInstant(v.vendedor_ubicacion_at);
  const liveMs = liveParsed ? liveParsed.getTime() : NaN;
  const hasLivePin =
    v.vendedor_lat != null &&
    v.vendedor_lng != null &&
    Number.isFinite(liveMs);

  if (hasLivePin) {
    lat = v.vendedor_lat;
    lng = v.vendedor_lng;
    at = v.vendedor_ubicacion_at;
  } else if (valid.length) {
    const last = valid[valid.length - 1];
    lat = last.lat;
    lng = last.lng;
    at = last.t || last.client_ts || null;
    accuracy = last.accuracy ?? null;
  } else if (v.lat_fin != null && v.lng_fin != null) {
    lat = v.lat_fin;
    lng = v.lng_fin;
    at = v.fin || null;
    puntosCount = 1;
  } else if (v.lat_inicio != null && v.lng_inicio != null) {
    lat = v.lat_inicio;
    lng = v.lng_inicio;
    at = v.inicio || null;
    puntosCount = 1;
  } else {
    return null;
  }

  if (!puntosCount && hasLivePin) puntosCount = 1;

  const ageSource = v.vendedor_ubicacion_at || at;
  const ageParsed = parseInstant(ageSource);
  const ageMs = ageParsed ? Date.now() - ageParsed.getTime() : Infinity;
  const stale = !Number.isFinite(ageMs) || ageMs > GPS_STALE_MS;

  return {
    lat,
    lng,
    puntos: puntosCount || 1,
    at: ageSource || at,
    accuracy: accuracy != null ? Number(accuracy) : null,
    stale,
    ageMs,
    ageLabel: fmtHace(ageSource),
  };
}

function routeUrl(puntos) {
  if (!puntos.length) return null;
  const valid = puntos.filter((p) => p.lat != null && p.lng != null);
  if (!valid.length) return null;
  if (valid.length === 1) return mapsUrl(valid[0].lat, valid[0].lng);
  // Google admite hasta ~10 waypoints; muestrear si hay muchos
  let path = valid;
  if (valid.length > 12) {
    const step = Math.ceil(valid.length / 10);
    path = valid.filter((_, i) => i === 0 || i === valid.length - 1 || i % step === 0);
  }
  const origin = `${path[0].lat},${path[0].lng}`;
  const dest = `${path[path.length - 1].lat},${path[path.length - 1].lng}`;
  const mid = path.slice(1, -1).map((p) => `${p.lat},${p.lng}`).join('|');
  let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&travelmode=driving`;
  if (mid) url += `&waypoints=${encodeURIComponent(mid)}`;
  return url;
}

function dedupePorVendedor(list) {
  const map = new Map();
  for (const v of list) {
    const key = v.vendedor_id
      ? `v${v.vendedor_id}`
      : (v.vendedor_nombre || '').trim().toLowerCase() || `i${v.id}`;
    const prev = map.get(key);
    if (!prev || v.id > prev.id) map.set(key, v);
  }
  return [...map.values()].sort((a, b) => new Date(b.inicio) - new Date(a.inicio));
}

function vendedorFilterKey(v) {
  if (v.vendedor_id) return `id:${v.vendedor_id}`;
  const nombre = (v.vendedor_nombre || '').trim();
  return nombre ? `n:${nombre.toLowerCase()}` : '';
}

function buildVendedorOptions(vendedores, visitas) {
  const map = new Map();
  for (const v of vendedores) {
    map.set(`id:${v.id}`, { key: `id:${v.id}`, nombre: v.nombre });
  }
  for (const visita of visitas) {
    const key = vendedorFilterKey(visita);
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, {
        key,
        nombre: visita.vendedor_nombre?.trim() || `Vendedor #${visita.vendedor_id}`,
      });
    }
  }
  return [...map.values()].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

function matchesVendedorFilter(visita, filter) {
  if (!filter) return true;
  if (filter.startsWith('id:')) {
    return visita.vendedor_id === Number(filter.slice(3));
  }
  if (filter.startsWith('n:')) {
    return (visita.vendedor_nombre || '').trim().toLowerCase() === filter.slice(2);
  }
  return true;
}

function minsBetween(inicio, fin) {
  if (!inicio) return 0;
  const start = parseInstant(inicio)?.getTime();
  if (!Number.isFinite(start)) return 0;
  const end = fin ? parseInstant(fin)?.getTime() : Date.now();
  if (!Number.isFinite(end)) return 0;
  return Math.max(0, Math.floor((end - start) / 60000));
}

function fmtDuracion(inicio, fin) {
  const mins = minsBetween(inicio, fin);
  if (!inicio) return '—';
  if (mins < 1) return 'Menos de 1 min';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function fmtMinsTotal(mins) {
  if (mins < 1) return '0 min';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function localISODate(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultDesde() {
  const d = new Date();
  return localISODate(new Date(d.getFullYear(), d.getMonth(), 1));
}

function defaultHasta() {
  return localISODate(new Date());
}

function VisitasActivasTable({ visitas, onOpenMap, onCerrar, cerrandoId }) {
  return (
    <div className="table-wrap visitas-table-wrap">
      <table className="visitas-table">
        <thead>
          <tr>
            <th>Vendedor</th>
            <th>Cliente / sucursal</th>
            <th>Ciudad</th>
            <th>Tiempo en cliente</th>
            <th>GPS</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {visitas.map((v) => {
            const ub = ultimaUbicacion(v);
            const puntos = parsePuntos(v.puntos_gps);
            const ruta = routeUrl(puntos.length ? puntos : ub ? [{ lat: ub.lat, lng: ub.lng }] : []);
            const puedeCerrar = !v.live_only && v.id && !String(v.id).startsWith('live-');

            return (
              <tr key={v.id}>
                <td className="visita-vendedor-cell" data-label="Vendedor">{v.vendedor_nombre || '—'}</td>
                <td data-label="Cliente">
                  <strong>
                    {fixText(v.display_nombre || v.sucursal_nombre) ||
                      v.sucursal_codigo ||
                      (v.live_only ? 'Última señal' : '—')}
                  </strong>
                  {v.es_sede || v.padre_codigo ? (
                    <span className="cliente-sede-pill">Sede</span>
                  ) : null}
                  {v.es_sede || v.padre_codigo ? (
                    <span className="visita-codigo">
                      Cliente {v.padre_codigo || '—'}
                      {v.sede_etiqueta ? ` · ${fixText(v.sede_etiqueta)}` : ''}
                    </span>
                  ) : v.sucursal_codigo ? (
                    <span className="visita-codigo">Cód. {v.sucursal_codigo}</span>
                  ) : null}
                  {v.live_only ? (
                    <span className="visita-codigo">Solo GPS (sin visita abierta)</span>
                  ) : null}
                </td>
                <td data-label="Ciudad">{fixText(v.ciudad) || '—'}</td>
                <td className="visita-tiempo-cell" data-label="Tiempo">
                  <strong>{fmtDuracion(v.inicio, null)}</strong>
                  <span className="visita-tiempo-hint">
                    {v.live_only ? 'desde última señal…' : 'tomando pedido…'}
                  </span>
                </td>
                <td className="visita-actions-cell" data-label="GPS">
                  {ub ? (
                    <>
                      <span
                        className={`visita-gps-freshness${ub.stale ? ' stale' : ' live'}`}
                        title={ub.at ? `Última señal: ${ub.at}` : undefined}
                      >
                        {gpsFreshnessText(ub)}
                        {ub.accuracy != null && Number.isFinite(ub.accuracy)
                          ? ` · ±${Math.round(ub.accuracy)} m`
                          : ''}
                      </span>
                      <button type="button" className="link-btn visitas-historial-btn" onClick={() => onOpenMap(v)}>
                        Historial mapa
                      </button>
                      <a href={mapsUrl(ub.lat, ub.lng)} target="_blank" rel="noreferrer">Google</a>
                      {ruta ? (
                        <a href={ruta} target="_blank" rel="noreferrer">Ruta</a>
                      ) : null}
                      <span className="visita-gps-count">{ub.puntos || 1} pts</span>
                    </>
                  ) : (
                    <span className="muted-inline">Sin GPS</span>
                  )}
                </td>
                <td className="visita-actions-cell" data-label="">
                  {puedeCerrar ? (
                    <button
                      type="button"
                      className="btn-ghost"
                      disabled={cerrandoId === v.id}
                      onClick={() => onCerrar?.(v)}
                    >
                      {cerrandoId === v.id ? 'Cerrando…' : 'Cerrar visita'}
                    </button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function VisitasHistorialTable({ visitas, onOpenMap }) {
  return (
    <div className="table-wrap visitas-table-wrap">
      <table className="visitas-table">
        <thead>
          <tr>
            <th>Vendedor</th>
            <th>Cliente</th>
            <th>Ciudad</th>
            <th>Tiempo en visita</th>
            <th>Pedido</th>
            <th>GPS</th>
          </tr>
        </thead>
        <tbody>
          {visitas.map((v) => {
            const ub = ultimaUbicacion(v);
            const puntos = parsePuntos(v.puntos_gps);
            const ruta = routeUrl(puntos.length ? puntos : ub ? [{ lat: ub.lat, lng: ub.lng }] : []);
            return (
              <tr key={v.id}>
                <td className="visita-vendedor-cell" data-label="Vendedor">{v.vendedor_nombre || '—'}</td>
                <td data-label="Cliente">
                  <strong>
                    {fixText(v.display_nombre || v.sucursal_nombre) || v.sucursal_codigo}
                  </strong>
                  {v.es_sede || v.padre_codigo ? (
                    <span className="cliente-sede-pill">Sede</span>
                  ) : null}
                  {v.es_sede || v.padre_codigo ? (
                    <span className="visita-codigo">
                      Cliente {v.padre_codigo || '—'}
                      {v.sede_etiqueta ? ` · ${fixText(v.sede_etiqueta)}` : ''}
                    </span>
                  ) : v.sucursal_codigo ? (
                    <span className="visita-codigo">Cód. {v.sucursal_codigo}</span>
                  ) : null}
                </td>
                <td data-label="Ciudad">{fixText(v.ciudad) || '—'}</td>
                <td className="visita-tiempo-cell" data-label="Tiempo">
                  <strong>{fmtDuracion(v.inicio, v.fin)}</strong>
                  {v.inicio && v.fin ? (
                    <span className="visita-tiempo-hint">
                      {formatColombiaTime(v.inicio)}
                      {' → '}
                      {formatColombiaTime(v.fin)}
                    </span>
                  ) : null}
                </td>
                <td data-label="Pedido">{v.pedido_numero || (v.pedido_id ? `#${v.pedido_id}` : '—')}</td>
                <td className="visita-actions-cell" data-label="GPS">
                  {ub ? (
                    <>
                      <button type="button" className="link-btn visitas-historial-btn" onClick={() => onOpenMap(v)}>
                        Historial mapa
                      </button>
                      <a href={mapsUrl(ub.lat, ub.lng)} target="_blank" rel="noreferrer">Google</a>
                      {ruta && (puntos.length >= 2 || ub) ? (
                        <a href={ruta} target="_blank" rel="noreferrer">Ruta</a>
                      ) : null}
                      <span className="visita-gps-count">{puntos.length || 1} pts</span>
                    </>
                  ) : (
                    <span className="muted-inline">Sin GPS</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function VisitasPage({ pin, onToast }) {
  const [tab, setTab] = useState('activas');
  const [visitas, setVisitas] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [vendedorFilter, setVendedorFilter] = useState('');
  const [desde, setDesde] = useState(defaultDesde);
  const [hasta, setHasta] = useState(defaultHasta);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [cleaning, setCleaning] = useState(false);
  const [cerrandoId, setCerrandoId] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [mapaVisita, setMapaVisita] = useState(null);
  const [recorridoFecha, setRecorridoFecha] = useState(defaultHasta);
  const [recorridoLoading, setRecorridoLoading] = useState(false);
  const [, tick] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    const opts =
      tab === 'historial'
        ? {
            estado: 'historial',
            desde,
            hasta,
            vendedor_id: vendedorFilter.startsWith('id:') ? vendedorFilter.slice(3) : undefined,
          }
        : { estado: 'activas' };
    api
      .getAdminVisitas(opts)
      .then((r) => {
        let rows = r.data || [];
        if (tab === 'activas') rows = dedupePorVendedor(rows);
        if (tab === 'historial' && vendedorFilter.startsWith('n:')) {
          rows = rows.filter((v) => matchesVendedorFilter(v, vendedorFilter));
        }
        setVisitas(rows);
        setLoadError('');
      })
      .catch((err) => {
        setVisitas([]);
        setLoadError(err.message || 'No se pudieron cargar las visitas');
      })
      .finally(() => setLoading(false));
  }, [pin, tab, desde, hasta, vendedorFilter]);

  useEffect(() => {
    api.getVendedores().then((r) => setVendedores(r.data || [])).catch(() => setVendedores([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useInvalidate(['visit', 'client', 'seller'], load);

  useEffect(() => {
    if (!autoRefresh || tab !== 'activas') return undefined;
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [autoRefresh, tab, load]);

  useEffect(() => {
    if (tab !== 'activas' || !visitas.length) return undefined;
    const t = setInterval(() => tick((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, [tab, visitas.length]);

  async function cerrarHuerfanas() {
    setCleaning(true);
    try {
      await api.cerrarVisitasHuerfanas();
      setLoadError('');
      load();
    } catch (err) {
      const msg = err.message || 'No se pudieron limpiar las visitas duplicadas';
      setLoadError(msg);
      onToast?.(msg, 'error');
    } finally {
      setCleaning(false);
    }
  }

  async function cerrarVisitaActiva(v) {
    if (!v?.id || String(v.id).startsWith('live-')) return;
    setCerrandoId(v.id);
    try {
      await api.cerrarVisitaAdmin(v.id);
      onToast?.('Visita cerrada');
      load();
    } catch (err) {
      onToast?.(err.message || 'No se pudo cerrar la visita', 'error');
    } finally {
      setCerrandoId(null);
    }
  }

  async function verRecorridoDia() {
    if (!vendedorFilter.startsWith('id:')) return;
    const vendedorId = vendedorFilter.slice(3);
    setRecorridoLoading(true);
    try {
      const r = await api.getRecorridoVendedor(vendedorId, recorridoFecha);
      const data = r.data || {};
      const puntos = data.puntos || [];
      if (!puntos.length) {
        onToast?.(`Sin recorrido registrado el ${data.fecha || recorridoFecha}`, 'info');
        return;
      }
      setMapaVisita({
        id: `recorrido-${vendedorId}-${data.fecha}`,
        vendedor_nombre: data.vendedor_nombre,
        sucursal_nombre: `Recorrido del ${data.fecha}`,
        puntos_gps: puntos,
      });
    } catch (err) {
      onToast?.(err.message || 'No se pudo cargar el recorrido', 'error');
    } finally {
      setRecorridoLoading(false);
    }
  }

  const vendedorOptions = buildVendedorOptions(vendedores, visitas);
  const visitasFiltradas =
    tab === 'historial'
      ? visitas
      : (vendedorFilter
          ? visitas.filter((v) => matchesVendedorFilter(v, vendedorFilter))
          : visitas
        ).filter(isActivaEnCampo);
  const enCurso = tab === 'activas' ? visitasFiltradas.length : 0;
  const vendedorLabel = vendedorOptions.find((v) => v.key === vendedorFilter)?.nombre;

  const stats = useMemo(() => {
    const list = visitasFiltradas;
    const totalMins = list.reduce((s, v) => s + minsBetween(v.inicio, v.fin || null), 0);
    const conGps = list.filter((v) => ultimaUbicacion(v)).length;
    const avg = list.length ? Math.round(totalMins / list.length) : 0;
    return { totalMins, conGps, avg, n: list.length };
  }, [visitasFiltradas]);

  return (
    <div className="panel">
      <header className="panel-head visitas-head">
        <div>
          <h2>Vendedores en campo</h2>
          <span>GPS en vivo, historial de ubicación y ruta de cada visita</span>
        </div>
        <div className="visitas-actions">
          {tab === 'activas' ? (
            <label className="check">
              <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
              Auto-actualizar
            </label>
          ) : null}
          <button type="button" className="btn-secondary" onClick={load}>
            Actualizar
          </button>
          <button type="button" className="btn-secondary danger" disabled={cleaning} onClick={cerrarHuerfanas}>
            {cleaning ? '...' : 'Limpiar duplicadas'}
          </button>
        </div>
      </header>

      <div className="tabs">
        <button type="button" className={tab === 'activas' ? 'active' : ''} onClick={() => setTab('activas')}>
          En campo {enCurso > 0 ? `(${enCurso})` : ''}
        </button>
        <button type="button" className={tab === 'historial' ? 'active' : ''} onClick={() => setTab('historial')}>
          Historial
        </button>
      </div>

      <div className="visitas-toolbar">
        <label className="visitas-filter">
          <span>Vendedor</span>
          <select value={vendedorFilter} onChange={(e) => setVendedorFilter(e.target.value)}>
            <option value="">Todos los vendedores</option>
            {vendedorOptions.map((v) => (
              <option key={v.key} value={v.key}>
                {v.nombre}
              </option>
            ))}
          </select>
        </label>
        {tab === 'historial' ? (
          <>
            <label className="visitas-filter">
              <span>Desde</span>
              <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
            </label>
            <label className="visitas-filter">
              <span>Hasta</span>
              <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
            </label>
          </>
        ) : null}
        {vendedorFilter.startsWith('id:') ? (
          <div className="recorrido-dia">
            <label className="visitas-filter">
              <span>Día</span>
              <input
                type="date"
                value={recorridoFecha}
                onChange={(e) => setRecorridoFecha(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn-secondary"
              disabled={recorridoLoading}
              onClick={verRecorridoDia}
            >
              {recorridoLoading ? 'Cargando…' : 'Recorrido del día'}
            </button>
          </div>
        ) : null}
        {vendedorFilter || tab === 'historial' ? (
          <span className="visitas-filter-count">
            {visitasFiltradas.length} visita{visitasFiltradas.length !== 1 ? 's' : ''}
            {vendedorLabel ? ` · ${vendedorLabel}` : ''}
          </span>
        ) : null}
      </div>

      {visitasFiltradas.length > 0 ? (
        <div className="stat-grid visitas-stats">
          <div className="stat-card">
            <span>Visitas</span>
            <strong>{stats.n}</strong>
          </div>
          <div className="stat-card stat-purple">
            <span>Tiempo total</span>
            <strong>{fmtMinsTotal(stats.totalMins)}</strong>
          </div>
          <div className="stat-card stat-orange">
            <span>Promedio / visita</span>
            <strong>{fmtMinsTotal(stats.avg)}</strong>
          </div>
          <div className="stat-card stat-green">
            <span>Con GPS</span>
            <strong>
              {stats.conGps}/{stats.n}
            </strong>
          </div>
        </div>
      ) : null}

      {tab === 'activas' && enCurso > 0 ? (
        <p className="visitas-live">
          <span className="live-dot" /> {enCurso} vendedor{enCurso > 1 ? 'es' : ''} en campo
        </p>
      ) : null}

      {loading && !visitas.length ? <p className="muted">Cargando...</p> : null}

      {loadError ? (
        <p className="error banner">
          {loadError}{' '}
          <button type="button" className="link-btn" onClick={load}>
            Reintentar
          </button>
        </p>
      ) : null}

      {!loading && !loadError && !visitasFiltradas.length ? (
        <p className="muted">
          {vendedorFilter
            ? 'Sin visitas para este vendedor.'
            : tab === 'activas'
              ? 'Nadie en campo ahora.'
              : 'Sin visitas cerradas en el historial para este rango.'}
        </p>
      ) : null}

      {tab === 'activas' && visitasFiltradas.length > 0 ? (
        <VisitasActivasTable
          visitas={visitasFiltradas}
          onOpenMap={setMapaVisita}
          onCerrar={cerrarVisitaActiva}
          cerrandoId={cerrandoId}
        />
      ) : null}

      {tab === 'historial' && visitasFiltradas.length > 0 ? (
        <VisitasHistorialTable visitas={visitasFiltradas} onOpenMap={setMapaVisita} />
      ) : null}

      {mapaVisita ? (
        <VisitaMapModal visita={mapaVisita} onClose={() => setMapaVisita(null)} />
      ) : null}
    </div>
  );
}
